/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  NumberInput,
  Group,
  Stack,
  Textarea,
  TextInput,
  Select,
  Switch,
  ActionIcon,
  Box,
  Paper,
  Text,
  Checkbox,
  MultiSelect,
  Image,
  FileButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useUpdateOrgSettings } from "api/organizations/updateOrg";
import { useGetOrg } from "api/organizations/getOrg";
import { useUploadFile } from "api/upload/uploadFile";
import { useQueryClient } from "@tanstack/react-query";
import { IconPlus, IconTrash, IconUpload, IconX } from "@tabler/icons-react";

type UpdateOrgFormProps = {
  orgId: string;
  onClose: () => void;
};

interface TimeConfig {
  id: string;
  date?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

interface RecurringConfig {
  id: string;
  recurrenceType: "day" | "weekly" | "biweekly" | "monthly";
  interval: number;
  selectedDays: string[];
  selectedDate?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

interface FormValues {
  orgName: string;
  timeConfigs: TimeConfig[];
  breakTimeEnabled: boolean;
  breakType: "duration" | "timeRange";
  breakDurationHours: number;
  breakStartTime?: string;
  breakEndTime?: string;

  overtimeAllowed: boolean;
  maxHoursPerDay: number;
  maxHoursPerMonth: number;
  overtimeRequiresApproval: boolean;
  overtimeRate: number;

  lateGraceMinutes: number;
  lateMarkHalfDayAfterMinutes: number;
  lateDeductionEnabled: boolean;
  lateDeductMinutesFor: number;
  lateForEveryMinutes: number;

  earlyGraceMinutes: number;
  earlyMarkHalfDayBeforeMinutes: number;
  earlyNotesRequired: boolean; // ✨ NEW

  // Leave-driven status automation
  leaveAutomationEnabled: boolean;
  shortTermLeaveMaxDays: number;
  longTermLeaveMinDays: number;

  handbook: string;

  // Location fields
  isLocationCheck: boolean;
  lng: string;
  lat: string;

  // Recurring configuration
  enableRecurringConfig: boolean;
  recurringConfigs: RecurringConfig[];
  // Logo
  logo: string;
  fiscalYearStartDate: string;
  fiscalYearEndDate: string;
  recurringStartDate: string;

  // ⭐ NEW: members
  members: string[];

  // Timezone
  timezone: string;
}

import { CANADA_TIMEZONES, DEFAULT_ORG_TIMEZONE } from "utils/timezoneDate";

const parseTimeToMinutes = (t?: string) => {
  if (!t) return null;
  const [h, m] = t.split(":");
  const hh = parseInt(h ?? "0", 10);
  const mm = parseInt(m ?? "0", 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const minutesToHours = (mins: number) => {
  return Math.round((mins / 60) * 100) / 100;
};

const pad = (n: number) => n.toString().padStart(2, "0");
const minutesToTimeString = (mins?: number | null) => {
  if (mins === null || mins === undefined) return undefined;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad(h)}:${pad(m)}`;
};

/** Helper getters: try multiple possible keys and coerce safely */
const getNumber = (obj: any, ...keys: string[]) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "number") return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return undefined;
};

const getBoolean = (obj: any, ...keys: string[]) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") {
      const lower = v.toLowerCase();
      if (lower === "true" || lower === "1") return true;
      if (lower === "false" || lower === "0") return false;
    }
  }
  return undefined;
};

const getString = (obj: any, ...keys: string[]) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
  }
  return undefined;
};

const isoToDateInput = (iso?: string | null) =>
  typeof iso === "string" && iso.length >= 10 ? iso.split("T")[0] : "";

export default function UpdateOrgForm({ orgId, onClose }: UpdateOrgFormProps) {
  const { data, refetch } = useGetOrg(orgId);
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useUpdateOrgSettings();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [handbookFile, setHandbookFile] = useState<File | null>(null);
  const [handbookPreview, setHandbookPreview] = useState<string>("");

  const form = useForm<FormValues>({
    initialValues: {
      orgName: "",
      timeConfigs: [],
      breakTimeEnabled: false,
      breakType: "duration",
      breakDurationHours: 1,
      breakStartTime: "13:00",
      breakEndTime: "14:00",

      overtimeAllowed: false,
      maxHoursPerDay: 2,
      maxHoursPerMonth: 20,
      overtimeRequiresApproval: true,
      overtimeRate: 1,

      lateGraceMinutes: 15,
      lateMarkHalfDayAfterMinutes: 120,
      lateDeductionEnabled: true,
      lateDeductMinutesFor: 5,
      lateForEveryMinutes: 15,

      earlyGraceMinutes: 10,
      earlyMarkHalfDayBeforeMinutes: 60,
      earlyNotesRequired: false, // ✨ NEW

      // Leave-driven status automation (effective defaults; unset = these)
      leaveAutomationEnabled: true,
      shortTermLeaveMaxDays: 5,
      longTermLeaveMinDays: 30,

      handbook: "",

      isLocationCheck: false,
      lng: "",
      lat: "",

      enableRecurringConfig: false,
      recurringConfigs: [],
      logo: "",
      fiscalYearStartDate: "",
      fiscalYearEndDate: "",
      recurringStartDate: "",

      // ⭐ members
      members: [],

      timezone: DEFAULT_ORG_TIMEZONE,
    },

    validate: {
      timeConfigs: {
        date: (value, values, path) => {
          const match = path.match(/timeConfigs\.(\d+)\.date/);
          if (match) {
            const index = parseInt(match[1], 10);
            if (index > 0 && !value) {
              return "Date is required for additional configurations";
            }
          }
          return null;
        },
      },
      recurringConfigs: {
        selectedDate: (value, values, path) => {
          if (values.enableRecurringConfig && !value) {
            return "Date is required for recurring configuration";
          }
          return null;
        },
        selectedDays: (value, values, path) => {
          const match = path?.match(/recurringConfigs\.(\d+)\.selectedDays/);
          if (match && values.enableRecurringConfig) {
            const index = parseInt(match[1], 10);
            const config = values.recurringConfigs[index];
            if (
              config &&
              config.recurrenceType !== "day" &&
              (!value || value.length === 0)
            ) {
              return "At least one day is required";
            }
          }
          return null;
        },
      },
    },
  });

  // Member options derived from API data
  const memberOptions = useMemo(() => {
    const arr =
      (data as any)?.members ||
      (data as any)?.orgMembers ||
      (data as any)?.employees ||
      [];
    if (!Array.isArray(arr)) return [];
    return arr.map((u: any) => ({
      value: String(u._id ?? u.id ?? u.userId ?? ""),
      label: u.firstName
        ? `${u.firstName} ${u.lastName ?? ""}`
        : (u.name ?? u.email ?? "Unknown"),
    }));
  }, [data]);

  // ⭐ MASTER ACCOUNT: derive master account (read-only display)
  const masterAccount = useMemo(() => {
    const ma =
      (data as any)?.master_account || (data as any)?.masterAccount || null;
    if (!ma) return null;
    return {
      id: String(ma._id ?? ma.id ?? ""),
      firstName: getString(ma, "firstName", "first_name") ?? "",
      lastName: getString(ma, "lastName", "last_name") ?? "",
      email: getString(ma, "email") ?? "",
    };
  }, [data]);

  // When API data arrives, map it safely into form values
  useEffect(() => {
    if (!data) return;

    console.log("API data (full):", data);

    const wh = (data as any).workingHours || (data as any).working_hours || [];
    const oc =
      (data as any).overtimeConfig || (data as any).overtime_config || {};
    const lac =
      (data as any).lateArrivalConfig ||
      (data as any).late_arrival_config ||
      {};
    const edc =
      (data as any).earlyDepartureConfig ||
      (data as any).early_departure_config ||
      {};
    const lsa = (data as any).leaveStatusAutomation || {};
    const handbookFromRoot =
      (data as any).handbook ??
      (data as any).handbookUrl ??
      (data as any).handbook_url;

    const isLocationCheck = getBoolean(data, "isLocationCheck") ?? false;
    const location = (data as any).location || {};
    const lng = getString(location, "lng", "lng") ?? "";
    const lat = getString(location, "lat", "lat") ?? "";

    // Handle members list from API
    const apiMembers =
      (data as any).members ??
      (data as any).orgMembers ??
      (data as any).employees ??
      [];
    const memberIds = Array.isArray(apiMembers)
      ? apiMembers.map((m: any) => String(m._id ?? m.id ?? m.userId ?? ""))
      : [];

    // Handle time configurations - separate default and recurring configs
    let timeConfigs: TimeConfig[] = [];
    let recurringConfigs: RecurringConfig[] = [];
    let enableRecurringConfig = false;

    if (Array.isArray(wh) && wh.length > 0) {
      wh.forEach((config: any) => {
        if (!config) return;

        if (config.type === "default") {
          timeConfigs.push({
            id: config._id || "default-1",
            startTime: getString(config, "startTime", "start_time") ?? "08:00",
            endTime: getString(config, "endTime", "end_time") ?? "16:00",
            totalHours: getNumber(config, "totalHours", "total_hours") ?? 8,
          });
        } else if (config.type === "recurring") {
          // Found a recurring configuration - add to array
          enableRecurringConfig = true;
          recurringConfigs.push({
            id: config._id || `recurring-${Date.now()}-${Math.random()}`,
            recurrenceType:
              (getString(config, "recurrenceType", "type") as any) || "weekly",
            interval: getNumber(config, "interval") ?? 1,
            selectedDays: Array.isArray(config.selectedDays)
              ? config.selectedDays
              : getString(config, "selectedDays")?.split(",") || [],
            selectedDate: getString(config, "date", "selectedDate") ?? "",
            startTime: getString(config, "startTime", "start_time") ?? "08:00",
            endTime: getString(config, "endTime", "end_time") ?? "16:00",
            totalHours: getNumber(config, "totalHours", "total_hours") ?? 8,
          });
        }
      });
    }

    // If no time configs found, create a default one
    if (timeConfigs.length === 0) {
      timeConfigs = [
        {
          id: "default-1",
          startTime: "08:00",
          endTime: "16:00",
          totalHours: 8,
        },
      ];
    }

    // Break info is stored at the root level of the organization, not inside workingHours
    const rootBreak = (data as any).break || (data as any).breakInfo || (data as any).break_info;
    const whBreak = wh?.break || wh?.breakInfo || wh?.break_info;
    const breakInfo = rootBreak || whBreak;

    const breakTimeEnabled = 
      breakInfo !== null && 
      breakInfo !== undefined && 
      typeof breakInfo === "object" &&
      Object.keys(breakInfo).length > 0 &&
      getBoolean(breakInfo, "enabled") === true;
    const breakType = getString(breakInfo, "type", "breakType") ?? "duration";
    const breakDurationHours =
      getNumber(
        breakInfo,
        "durationHours",
        "duration_hours",
        "durationMinutes",
        "duration_minutes",
      ) ??
      (breakInfo && typeof breakInfo.durationMinutes === "number"
        ? Math.round((breakInfo.durationMinutes / 60) * 100) / 100
        : 1);

    const breakStartTime =
      getString(breakInfo, "startTime", "start_time") ??
      minutesToTimeString(
        getNumber(breakInfo, "startMinutes", "start_minutes") ?? undefined,
      );

    const breakEndTime =
      getString(breakInfo, "endTime", "end_time") ??
      minutesToTimeString(
        getNumber(breakInfo, "endMinutes", "end_minutes") ?? undefined,
      );

    const lateGraceMinutes =
      getNumber(lac, "graceMinutes", "grace_minutes", "grace") ?? 15;
    const lateMarkHalfDayAfterMinutes =
      getNumber(
        lac,
        "markHalfDayAfterMinutes",
        "mark_half_day_after_minutes",
        "markHalfDayAfter",
      ) ?? 120;
    const lateDeductionEnabled =
      getBoolean(lac, "deductionEnabled", "deduction_enabled", "deduction") ??
      true;
    const lateDeductMinutesFor =
      getNumber(
        lac,
        "deductMinutesFor",
        "deduct_minutes_for",
        "deductMinutes",
      ) ?? 5;
    const lateForEveryMinutes =
      getNumber(lac, "forEveryMinutes", "for_every_minutes", "forEvery") ?? 15;

    const earlyGraceMinutes =
      getNumber(edc, "graceMinutes", "grace_minutes", "grace") ?? 10;
    const earlyMarkHalfDayBeforeMinutes =
      getNumber(
        edc,
        "markHalfDayBeforeMinutes",
        "mark_half_day_before_minutes",
        "markHalfDayBefore",
      ) ?? 60;
    const earlyNotesRequired =
      getBoolean(edc, "notesRequired", "notes_required") ?? false; // ✨ NEW

    const handbook =
      getString(data, "handbook", "handbookUrl", "handbook_url") ??
      handbookFromRoot ??
      "";

    const logo = getString(data, "logo") ?? "";

    const fiscalObj =
      (data as any).fiscalYear || (data as any).fiscal_year || {};
    const rawFiscalStart =
      getString(fiscalObj, "startDate", "start_date") ||
      getString(data, "fiscalYearStartDate", "fiscal_year_start_date") ||
      "";
    const rawFiscalEnd =
      getString(fiscalObj, "endDate", "end_date") ||
      getString(data, "fiscalYearEndDate", "fiscal_year_end_date") ||
      "";

    const fiscalYearStartDate = isoToDateInput(rawFiscalStart);
    const fiscalYearEndDate = isoToDateInput(rawFiscalEnd);

    const rawRecurringStart =
      getString(data, "recurringStartDate", "recurring_start_date") ||
      getString(data, "recurringStart", "recurring_start") ||
      "";
    const recurringStartDate = isoToDateInput(rawRecurringStart);

    // SET ALL VALUES AT ONCE
    setTimeout(() => {
      form.setValues({
        orgName: getString(data, "name") ?? "",
        timeConfigs,
        breakTimeEnabled,
        breakType: breakType as any,
        breakDurationHours,
        breakStartTime: breakStartTime ?? "13:00",
        breakEndTime: breakEndTime ?? "14:00",
        overtimeAllowed: getBoolean(oc, "allowed", "isAllowed") ?? false,
        maxHoursPerDay:
          getNumber(oc, "maxHoursPerDay", "max_hours_per_day") ?? 2,
        maxHoursPerMonth:
          getNumber(oc, "maxHoursPerMonth", "max_hours_per_month") ?? 20,
        overtimeRequiresApproval:
          getBoolean(oc, "requiresApproval", "requires_approval") ?? true,
        overtimeRate: getNumber(oc, "overtimeRate", "overtime_Rate") ?? 1,
        lateGraceMinutes,
        lateMarkHalfDayAfterMinutes,
        lateDeductionEnabled,
        lateDeductMinutesFor,
        lateForEveryMinutes,
        earlyGraceMinutes,
        earlyMarkHalfDayBeforeMinutes,
        earlyNotesRequired, // ✨ NEW
        leaveAutomationEnabled:
          typeof lsa.enabled === "boolean" ? lsa.enabled : true,
        shortTermLeaveMaxDays:
          typeof lsa.shortTermLeaveMaxDays === "number"
            ? lsa.shortTermLeaveMaxDays
            : 5,
        longTermLeaveMinDays:
          typeof lsa.longTermLeaveMinDays === "number"
            ? lsa.longTermLeaveMinDays
            : 30,
        handbook,
        isLocationCheck,
        lng,
        lat,
        enableRecurringConfig,
        recurringConfigs,
        logo,
        fiscalYearStartDate,
        fiscalYearEndDate,
        recurringStartDate,

        // members from API
        members: [],

        timezone: getString(data, "timezone") ?? DEFAULT_ORG_TIMEZONE,
      });
    }, 0);
  }, [data]); // Only depend on data

  // Auto total hours calculation for default time config (without subtracting break hours)
  useEffect(() => {
    const updatedConfigs = form.values.timeConfigs.map((config) => {
      const s = parseTimeToMinutes(config.startTime);
      const e = parseTimeToMinutes(config.endTime);
      if (s === null || e === null || e <= s) {
        return { ...config, totalHours: 0 };
      }

      // Calculate total hours without subtracting break hours
      const workingMinutes = Math.max(0, e - s);
      const hours = minutesToHours(workingMinutes);
      return { ...config, totalHours: hours };
    });

    // Only update if there are changes to avoid infinite loop
    if (
      JSON.stringify(updatedConfigs) !== JSON.stringify(form.values.timeConfigs)
    ) {
      form.setFieldValue("timeConfigs", updatedConfigs);
    }
  }, [
    form.values.timeConfigs.map((c) => c.startTime).join(","),
    form.values.timeConfigs.map((c) => c.endTime).join(","),
  ]);

  // Auto total hours calculation for recurring configs (without subtracting break hours)
  useEffect(() => {
    if (form.values.enableRecurringConfig) {
      const updatedConfigs = form.values.recurringConfigs.map((config) => {
        const s = parseTimeToMinutes(config.startTime);
        const e = parseTimeToMinutes(config.endTime);

        if (s !== null && e !== null && e > s) {
          const workingMinutes = Math.max(0, e - s);
          const hours = minutesToHours(workingMinutes);
          return { ...config, totalHours: hours };
        }
        return config;
      });

      // Only update if there are changes to avoid infinite loop
      if (
        JSON.stringify(updatedConfigs) !==
        JSON.stringify(form.values.recurringConfigs)
      ) {
        form.setFieldValue("recurringConfigs", updatedConfigs);
      }
    }
  }, [
    form.values.recurringConfigs.map((c) => c.startTime).join(","),
    form.values.recurringConfigs.map((c) => c.endTime).join(","),
    form.values.enableRecurringConfig,
  ]);

  const addRecurringConfig = () => {
    form.insertListItem("recurringConfigs", {
      id: `recurring-${Date.now()}-${Math.random()}`,
      recurrenceType: "weekly",
      interval: 1,
      selectedDays: [],
      selectedDate: "",
      startTime: "08:00",
      endTime: "16:00",
      totalHours: 8,
    });
  };

  const removeRecurringConfig = (index: number) => {
    form.removeListItem("recurringConfigs", index);
  };

  // Auto-add first recurring config when checkbox is enabled with empty array
  useEffect(() => {
    if (
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length === 0
    ) {
      addRecurringConfig();
    }
  }, [form.values.enableRecurringConfig]);

  const handleSubmit = async (values: FormValues) => {
    let finalLogoUrl = values.logo;
    let finalHandbookUrl = values.handbook;

    // Upload logo file if selected
    if (logoFile) {
      try {
        const logoResponse = await new Promise<any>((resolve, reject) => {
          uploadFile(logoFile, {
            onSuccess: (response) => resolve(response),
            onError: (error) => reject(error),
          });
        });
        finalLogoUrl = logoResponse.data.url;
      } catch (error) {
        console.error("Logo upload failed:", error);
        // Continue with existing logo
      }
    }

    // Upload handbook file if selected
    if (handbookFile) {
      try {
        const handbookResponse = await new Promise<any>((resolve, reject) => {
          uploadFile(handbookFile, {
            onSuccess: (response) => resolve(response),
            onError: (error) => reject(error),
          });
        });
        finalHandbookUrl = handbookResponse.data.url;
      } catch (error: any) {
        import("@mantine/notifications").then(({ showNotification }) => {
          showNotification({
            title: "Handbook upload failed",
            message:
              error?.response?.data?.message ||
              "Failed to upload handbook file",
            color: "red",
          });
        });
        return;
      }
    }

    submitWithFiles(values, finalLogoUrl, finalHandbookUrl);
  };

  const submitWithFiles = (
    values: FormValues,
    logoUrl: string,
    handbookUrl: string,
  ) => {
    // Build timeConfigs array with proper structure
    const timeConfigs = values.timeConfigs.map((config) => ({
      type: "default" as const,
      startTime: config.startTime,
      endTime: config.endTime,
      totalHours: config.totalHours,
    }));

    // If recurring configs are enabled, add ALL of them
    if (values.enableRecurringConfig) {
      values.recurringConfigs.forEach((recurringConfig) => {
        const recurringTimeConfig: any = {
          type: "recurring" as const,
          recurringenabled: true,
          recurrenceType: recurringConfig.recurrenceType,
          interval: recurringConfig.interval,
          date: recurringConfig.selectedDate,
          startTime: recurringConfig.startTime,
          endTime: recurringConfig.endTime,
          totalHours: recurringConfig.totalHours,
        };

        // Add selected days only for non-day recurrence types
        if (recurringConfig.recurrenceType !== "day") {
          recurringTimeConfig.selectedDays = recurringConfig.selectedDays;
        }

        // Handle monthly pattern for monthly recurrence
        if (recurringConfig.recurrenceType === "monthly") {
          recurringTimeConfig.monthlyPattern = "first";
        }

        timeConfigs.push(recurringTimeConfig);
      });
    }

    const payload: any = {
      name: values.orgName,
      workingHours: {
        timeConfigs,
        break: values.breakTimeEnabled ? {
          enabled: true,
          type: values.breakType,
          durationHours:
            values.breakType === "duration"
              ? values.breakDurationHours
              : undefined,
          startTime:
            values.breakType === "timeRange"
              ? values.breakStartTime
              : undefined,
          endTime:
            values.breakType === "timeRange" ? values.breakEndTime : undefined,
        } : undefined,
      },
      overtimeConfig: {
        allowed: values.overtimeAllowed,
        maxHoursPerDay: values.maxHoursPerDay,
        maxHoursPerMonth: values.maxHoursPerMonth,
        requiresApproval: values.overtimeRequiresApproval,
        overtimeRate: values.overtimeRate,
      },
      lateArrivalConfig: {
        graceMinutes: values.lateGraceMinutes,
        markHalfDayAfterMinutes: values.lateMarkHalfDayAfterMinutes,
        deductionEnabled: values.lateDeductionEnabled,
        deductMinutesFor: values.lateDeductMinutesFor,
        forEveryMinutes: values.lateForEveryMinutes,
      },
      earlyDepartureConfig: {
        graceMinutes: values.earlyGraceMinutes,
        markHalfDayBeforeMinutes: values.earlyMarkHalfDayBeforeMinutes,
        notesRequired: values.earlyNotesRequired, // ✨ NEW
      },
      leaveStatusAutomation: {
        enabled: values.leaveAutomationEnabled,
        shortTermLeaveMaxDays: values.shortTermLeaveMaxDays,
        longTermLeaveMinDays: values.longTermLeaveMinDays,
      },
      handbook: handbookUrl || null,
      isLocationCheck: values.isLocationCheck,
      location: values.isLocationCheck
        ? {
            lng: values.lng,
            lat: values.lat,
          }
        : {},
      logo: logoUrl,
      fiscalYearStartDate: values.fiscalYearStartDate,
      fiscalYearEndDate: values.fiscalYearEndDate,
      recurringStartDate: values.recurringStartDate,

      // ⭐ include members if any
      members:
        values.members && values.members.length > 0
          ? values.members
          : undefined,

      timezone: values.timezone || DEFAULT_ORG_TIMEZONE,
    };

    console.log("Submitting payload:", JSON.stringify(payload, null, 2));

    mutate({ orgId, payload }, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["org", orgId]);
        setHandbookFile(null);
        setHandbookPreview("");
        setLogoFile(null);
        refetch?.();
        onClose();
      },
      onError: (err: any) => {
        console.error("Update failed:", err);
      },
    } as any);
  };

  const dayOptions = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing="md">
        <TextInput
          label="Organization Name"
          placeholder="Enter organization name"
          {...form.getInputProps("orgName")}
        />

        <Select
          label="Timezone"
          description="All attendance start/end times are interpreted in this timezone"
          placeholder="Select timezone"
          data={CANADA_TIMEZONES}
          searchable
          {...form.getInputProps("timezone")}
        />

        {masterAccount && (
          <Paper p="md" withBorder radius="md">
            <Text weight={500} mb="xs">
              Master Account Information
            </Text>

            <Group spacing="sm" align="stretch" grow>
              <TextInput
                label="First Name"
                value={`${masterAccount.firstName} `}
                readOnly
                disabled
              />
              <TextInput
                label="Last Name"
                value={`${masterAccount.lastName}`}
                readOnly
                disabled
              />

              <TextInput
                label="Email"
                value={masterAccount.email}
                readOnly
                disabled
              />
            </Group>
          </Paper>
        )}

        <h3>Working Hours Configuration</h3>

        {form.values.timeConfigs.map((config, index) => (
          <Paper key={config.id} p="md" withBorder>
            <Group position="apart" mb="sm">
              <Text weight={500}>Default Configuration</Text>
            </Group>

            <Group grow>
              <TextInput
                label="Start Time"
                type="time"
                {...form.getInputProps(`timeConfigs.${index}.startTime`)}
              />
              <TextInput
                label="End Time"
                type="time"
                {...form.getInputProps(`timeConfigs.${index}.endTime`)}
              />
              <NumberInput
                label="Total Hours"
                step={0.25}
                precision={2}
                {...form.getInputProps(`timeConfigs.${index}.totalHours`)}
              />
            </Group>
          </Paper>
        ))}

        <Checkbox
          label="Enable recurring time configurations"
          {...form.getInputProps("enableRecurringConfig", { type: "checkbox" })}
        />

        {form.values.enableRecurringConfig && (
          <>
            {form.values.recurringConfigs.map((config, index) => (
              <Paper key={config.id} p="md" withBorder>
                <Group position="apart" mb="md">
                  <Text weight={500}>Recurring Configuration {index + 1}</Text>
                  {form.values.recurringConfigs.length > 1 && (
                    <ActionIcon
                      color="red"
                      variant="light"
                      onClick={() => removeRecurringConfig(index)}
                      title="Remove recurring configuration"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>

                <Group grow>
                  <Select
                    label="Recurrence Type"
                    data={[
                      { value: "day", label: "Date" },
                      { value: "weekly", label: "Week" },
                      { value: "biweekly", label: "Bi-Week" },
                    ]}
                    {...form.getInputProps(
                      `recurringConfigs.${index}.recurrenceType`,
                    )}
                  />
                  <NumberInput
                    label="Interval"
                    min={1}
                    {...form.getInputProps(
                      `recurringConfigs.${index}.interval`,
                    )}
                  />
                  <TextInput
                    label="Select Date"
                    type="date"
                    required
                    {...form.getInputProps(
                      `recurringConfigs.${index}.selectedDate`,
                    )}
                  />
                </Group>

                {form.values.recurringConfigs[index].recurrenceType !==
                  "day" && (
                  <Group grow mt="md">
                    <MultiSelect
                      label="Selected Days"
                      data={dayOptions}
                      placeholder="Select days"
                      required
                      {...form.getInputProps(
                        `recurringConfigs.${index}.selectedDays`,
                      )}
                    />
                  </Group>
                )}

                <Group grow mt="md">
                  <TextInput
                    label="Start Time"
                    type="time"
                    {...form.getInputProps(
                      `recurringConfigs.${index}.startTime`,
                    )}
                  />
                  <TextInput
                    label="End Time"
                    type="time"
                    {...form.getInputProps(`recurringConfigs.${index}.endTime`)}
                  />
                  <NumberInput
                    label="Total Hours"
                    readOnly
                    step={0.25}
                    precision={2}
                    {...form.getInputProps(
                      `recurringConfigs.${index}.totalHours`,
                    )}
                  />
                </Group>
              </Paper>
            ))}

            <Button
              variant="outline"
              leftIcon={<IconPlus size={16} />}
              onClick={addRecurringConfig}
              type="button"
              mt="md"
            >
              Add Recurring Configuration
            </Button>
          </>
        )}

        <h3>Break Time Configuration</h3>

        <Switch
          label="Break Time Enabled"
          {...form.getInputProps("breakTimeEnabled", { type: "checkbox" })}
        />

        <Group align="flex-end">
          <Select
            label="Break Type"
            data={[
              { value: "duration", label: "Duration" },
              { value: "timeRange", label: "Time Range" },
            ]}
            {...form.getInputProps("breakType")}
            disabled={!form.values.breakTimeEnabled}
            sx={{ minWidth: 200 }}
          />
          {form.values.breakType === "duration" ? (
            <NumberInput
              label="Break Duration (hours)"
              min={0}
              precision={2}
              step={0.25}
              disabled={!form.values.breakTimeEnabled}
              {...form.getInputProps("breakDurationHours")}
            />
          ) : (
            <>
              <TextInput
                label="Break Start Time"
                type="time"
                disabled={!form.values.breakTimeEnabled}
                {...form.getInputProps("breakStartTime")}
              />
              <TextInput
                label="Break End Time"
                type="time"
                disabled={!form.values.breakTimeEnabled}
                {...form.getInputProps("breakEndTime")}
              />
            </>
          )}
        </Group>

        <hr />

        <h3>Overtime Configuration</h3>

        <Switch
          label="Overtime Allowed"
          {...form.getInputProps("overtimeAllowed", { type: "checkbox" })}
        />

        <Group align="flex-end">
          <NumberInput
            label="Max Hours Per Day"
            min={0}
            precision={2}
            disabled={!form.values.overtimeAllowed}
            {...form.getInputProps("maxHoursPerDay")}
          />
          <NumberInput
            label="Max Hours Per Month"
            min={0}
            precision={1}
            disabled={!form.values.overtimeAllowed}
            {...form.getInputProps("maxHoursPerMonth")}
          />
          <NumberInput
            label="Overtime Rate"
            min={0}
            precision={1}
            disabled={!form.values.overtimeAllowed}
            {...form.getInputProps("overtimeRate")}
          />
          <Switch
            label="Requires Approval"
            disabled={!form.values.overtimeAllowed}
            {...form.getInputProps("overtimeRequiresApproval", {
              type: "checkbox",
            })}
          />
        </Group>

        <hr />

        <h3>Late Arrival Configuration</h3>
        <Group>
          <NumberInput
            label="Grace Minutes"
            min={0}
            {...form.getInputProps("lateGraceMinutes")}
          />
          <NumberInput
            label="Mark as Half Day After (minutes)"
            min={0}
            {...form.getInputProps("lateMarkHalfDayAfterMinutes")}
          />
          <Switch
            label="Deduction Enabled"
            {...form.getInputProps("lateDeductionEnabled", {
              type: "checkbox",
            })}
          />
          <NumberInput
            label="Deduct Minutes For"
            min={0}
            disabled={!form.values.lateDeductionEnabled}
            {...form.getInputProps("lateDeductMinutesFor")}
          />
          <NumberInput
            label="For Every Minutes"
            min={1}
            disabled={!form.values.lateDeductionEnabled}
            {...form.getInputProps("lateForEveryMinutes")}
          />
        </Group>

        <hr />

        <h3>Early Departure Configuration</h3>
        <Group>
          <NumberInput
            label="Grace Minutes"
            min={0}
            {...form.getInputProps("earlyGraceMinutes")}
          />
          <NumberInput
            label="Mark as Half Day Before (minutes)"
            min={0}
            {...form.getInputProps("earlyMarkHalfDayBeforeMinutes")}
          />
          <Switch
            label="Notes Required for Early Departure"
            {...form.getInputProps("earlyNotesRequired", { type: "checkbox" })}
          />
        </Group>

        <hr />

        <h3>Leave Status Automation</h3>
        <Text size="xs" color="dimmed" mb={8}>
          Automatically set an employee to Inactive while on a qualifying leave.
          A leave up to the short-term limit auto-sets Inactive; a leave of at
          least the long-term limit also pauses annual-leave accrual.
        </Text>
        <Switch
          label="Enable leave-driven status automation"
          mb={8}
          {...form.getInputProps("leaveAutomationEnabled", { type: "checkbox" })}
        />
        <Group>
          <NumberInput
            label="Short-term leave: maximum days"
            min={0}
            disabled={!form.values.leaveAutomationEnabled}
            {...form.getInputProps("shortTermLeaveMaxDays")}
          />
          <NumberInput
            label="Long-term leave: minimum days"
            min={0}
            disabled={!form.values.leaveAutomationEnabled}
            {...form.getInputProps("longTermLeaveMinDays")}
          />
        </Group>

        <hr />

        <h3>Location</h3>
        <Checkbox
          label="Enable Location Check"
          {...form.getInputProps("isLocationCheck", { type: "checkbox" })}
        />

        {form.values.isLocationCheck && (
          <Group grow>
            <TextInput
              label="Longitude"
              placeholder="Enter longitude"
              {...form.getInputProps("lng")}
            />
            <TextInput
              label="Latitude"
              placeholder="Enter latitude"
              {...form.getInputProps("lat")}
            />
          </Group>
        )}

        <Box mb="md">
          <Text size="sm" weight={500} mb={5}>
            Handbook (PDF/Document - Max 5MB)
          </Text>

          {form.values.handbook && !handbookFile && (
            <Box
              mb="sm"
              p="sm"
              sx={{ backgroundColor: "#f8f9fa", borderRadius: 4 }}
            >
              <Text size="sm" weight={500} mb={4}>
                Current Handbook:
              </Text>
              <Group spacing="xs">
                <Text
                  component="a"
                  href={form.values.handbook}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue"
                  size="sm"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  View Handbook
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    form.setFieldValue("handbook", "");
                    setHandbookFile(null);
                    setHandbookPreview("");
                  }}
                >
                  Remove
                </Button>
              </Group>
            </Box>
          )}

          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setHandbookFile(null);
                setHandbookPreview("");
                return;
              }

              // Validate file size (5MB = 5 * 1024 * 1024 bytes)
              const maxSize = 5 * 1024 * 1024;
              if (file.size > maxSize) {
                import("@mantine/notifications").then(
                  ({ showNotification }) => {
                    showNotification({
                      title: "File too large",
                      message: "Handbook file must be less than 5MB",
                      color: "red",
                    });
                  },
                );
                e.target.value = "";
                return;
              }

              setHandbookFile(file);
              setHandbookPreview(file.name);
            }}
          />

          {handbookPreview && (
            <Text size="sm" color="blue" mt={5}>
              New file selected: {handbookPreview}
            </Text>
          )}
        </Box>

        <hr />

        <h3>Organization Logo</h3>
        <Box>
          {form.values.logo && !logoFile && (
            <Box mb="md">
              <Text size="sm" weight={500} mb="xs">
                Current Logo
              </Text>
              <Group spacing="md" align="center">
                <Image
                  src={form.values.logo}
                  alt="Organization logo"
                  width={80}
                  height={80}
                  fit="contain"
                  withPlaceholder
                  styles={{
                    root: { maxWidth: 80 },
                    image: { objectFit: "contain" },
                  }}
                />
                <ActionIcon
                  color="red"
                  variant="light"
                  onClick={() => {
                    form.setFieldValue("logo", "");
                    setLogoFile(null);
                  }}
                  title="Remove logo"
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Box>
          )}

          {logoFile && (
            <Box mb="md">
              <Text size="sm" weight={500} mb="xs">
                New Logo Preview
              </Text>
              <Group spacing="md" align="center">
                <Image
                  src={URL.createObjectURL(logoFile)}
                  alt="New logo preview"
                  width={80}
                  height={80}
                  fit="contain"
                  withPlaceholder
                  styles={{
                    root: { maxWidth: 80 },
                    image: { objectFit: "contain" },
                  }}
                />
                <ActionIcon
                  color="red"
                  variant="light"
                  onClick={() => setLogoFile(null)}
                  title="Remove new logo"
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Box>
          )}

          <FileButton
            onChange={(file) => {
              if (file) {
                setLogoFile(file);
              }
            }}
            accept="image/png,image/jpeg,image/jpg,image/gif"
          >
            {(props) => (
              <Button
                {...props}
                leftIcon={<IconUpload size={16} />}
                variant="light"
              >
                {logoFile || form.values.logo ? "Change Logo" : "Upload Logo"}
              </Button>
            )}
          </FileButton>
          <Text size="xs" color="dimmed" mt="xs">
            Recommended: PNG, JPG, or GIF (max 10MB, 80x80px will be displayed)
          </Text>
        </Box>
      </Stack>
      <hr />

      <h3>Fiscal Year & Recurring Dates</h3>
      <Group grow>
        <TextInput
          label="Fiscal Year Start Date"
          type="date"
          {...form.getInputProps("fiscalYearStartDate")}
        />
        <TextInput
          label="Fiscal Year End Date"
          type="date"
          {...form.getInputProps("fiscalYearEndDate")}
        />
        <TextInput
          label="Recurring Start Date"
          type="date"
          {...form.getInputProps("recurringStartDate")}
        />
      </Group>

      <Group position="right" mt="xl">
        <Button
          // variant="outline"
          color="cancel"
          onClick={() => {
            form.reset();
            setLogoFile(null);
            setHandbookFile(null);
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button type="submit" loading={isLoading || isUploading}>
          Save
        </Button>
      </Group>
    </form>
  );
}
